import { BuildUri } from './BuildUri'

import { PatchRequest } from '../requests/PatchRequest'
import {
  EventRequest,
  DuplicateEventRequest,
  PositionRequest,
  LocationRequest,
  IdentityRoleRequest,
  AccreditationLevelRequest,
} from '../requests/EventRequest'

import { ChildCreationResult } from '../model/ChildCreationResult'
import {
  Event,
  Position,
  SimpleEvent,
  IdentityRole,
  AccreditationLevel,
  GenericIdentityGroup,
  SimpleEventWithProvisionings,
  BadgeStatus,
} from '../model/Event'

import { NetworkManager } from '../context'
import { GenericIdentityGroupRequest } from '../requests/GenericIdentityGroupRequest'

class EventEndpoints {
  getEventList(
    fast: boolean = true,
    images: boolean = false
  ): Promise<Array<SimpleEvent>> {
    return NetworkManager.fetch(BuildUri('/event', {}, {}))
  }

  getEventListFull(includeRoleImages: boolean = false): Promise<Array<Event>> {
    return NetworkManager.fetch(
      BuildUri('/event', {}, { fast: false, includeRoleImages })
    )
  }

  getEventListForProvisioning(
    clientId?: number
  ): Promise<Array<SimpleEventWithProvisionings>> {
    return NetworkManager.fetch(
      BuildUri('/event', {}, { forProvisionings: true, clientId })
    )
  }

  getEvent(eventId: number): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}', { eventId }, { includeSchedule: false })
    )
  }

  createEvent(request: EventRequest): Promise<Event> {
    return NetworkManager.fetch(BuildUri('/event'), {
      method: 'POST',
      body: request,
    })
  }

  duplicateEvent(
    eventId: number,
    request: DuplicateEventRequest
  ): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/duplicate', { eventId }),
      {
        method: 'POST',
        body: request,
      }
    )
  }

  editEvent(eventId: number, request: PatchRequest): Promise<Event> {
    return NetworkManager.fetch(BuildUri('/event/{eventId}', { eventId }), {
      method: 'PATCH',
      body: request,
    })
  }

  discardEvent(eventId: number): Promise<void> {
    return NetworkManager.fetch(BuildUri('/event/{eventId}', { eventId }), {
      method: 'DELETE',
    })
  }

  addEventLocation(
    eventId: number,
    request: LocationRequest
  ): Promise<ChildCreationResult<Event, Location>> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/location', { eventId }),
      {
        method: 'POST',
        body: request,
      }
    )
  }

  editEventLocation(locationId: number, request: PatchRequest): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/location/{locationId}', { locationId }),
      {
        method: 'PATCH',
        body: request,
      }
    )
  }

  deleteEventLocation(locationId: number): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/location/{locationId}', { locationId }),
      {
        method: 'DELETE',
      }
    )
  }

  addEventPosition(
    eventId: number,
    request: PositionRequest
  ): Promise<ChildCreationResult<Event, Position>> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/position', { eventId }),
      {
        method: 'POST',
        body: request,
      }
    )
  }

  editEventPosition(positionId: number, request: PatchRequest): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/position/{positionId}', { positionId }),
      {
        method: 'PATCH',
        body: request,
      }
    )
  }

  deleteEventPosition(positionId: number): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/position/{positionId}', { positionId }),
      {
        method: 'DELETE',
      }
    )
  }

  addEventAccreditationLevel(
    eventId: number,
    request: AccreditationLevelRequest
  ): Promise<ChildCreationResult<Event, AccreditationLevel>> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/accreditation', { eventId }),
      {
        method: 'POST',
        body: request,
      }
    )
  }

  editEventAccreditationLevel(
    accreditationId: number,
    request: PatchRequest
  ): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/accreditation/{accreditationId}', {
        accreditationId,
      }),
      {
        method: 'PATCH',
        body: request,
      }
    )
  }

  deleteEventAccreditationLevel(accreditationId: number): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/accreditation/{accreditationId}', {
        accreditationId,
      }),
      {
        method: 'DELETE',
      }
    )
  }

  addEventRole(
    eventId: number,
    request: IdentityRoleRequest
  ): Promise<ChildCreationResult<Event, IdentityRole>> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/role', { eventId }),
      {
        method: 'POST',
        body: request,
      }
    )
  }

  editEventRole(roleId: number, request: IdentityRoleRequest): Promise<Event> {
    return NetworkManager.fetch(BuildUri('/event/role/{roleId}', { roleId }), {
      method: 'PUT',
      body: request,
    })
  }

  deleteEventRole(roleId: number): Promise<Event> {
    return NetworkManager.fetch(BuildUri('/event/role/{roleId}', { roleId }), {
      method: 'DELETE',
    })
  }

  addGenericIdentityGroup(
    eventId: number,
    request: GenericIdentityGroupRequest
  ): Promise<ChildCreationResult<Event, GenericIdentityGroup>> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/generic', { eventId }),
      {
        method: 'POST',
        body: request,
      }
    )
  }

  editGenericIdentityGroup(
    groupId: number,
    request: GenericIdentityGroupRequest
  ): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/generic/{groupId}', { groupId }),
      {
        method: 'PUT',
        body: request,
      }
    )
  }

  archiveGenericIdentityGroup(groupId: number): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/generic/{groupId}', { groupId }),
      {
        method: 'PATCH',
        body: {
          field: 'archived',
          value: 'true',
        },
      }
    )
  }

  unarchiveGenericIdentityGroup(groupId: number): Promise<Event> {
    return NetworkManager.fetch(
      BuildUri('/event/generic/{groupId}', { groupId }),
      {
        method: 'PATCH',
        body: {
          field: 'archived',
          value: 'false',
        },
      }
    )
  }

  getGenericIdentityGroupBadges(groupId: number) {
    return NetworkManager.fetch(
      BuildUri('/event/generic/{groupId}/badges', { groupId })
    )
  }

  getGenericIdentityGroupBadgeStatus(
    groupId: number
  ): Promise<{ id: number; version: number; badgeStatus: BadgeStatus }> {
    return NetworkManager.fetch(
      BuildUri('/event/generic/{groupId}/badge-status', { groupId })
    )
  }

  getBadgePreview(eventId: number, roleId?: number) {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/badge/preview', { eventId }, { roleId })
    )
  }

  exportBadges(
    eventId: number,
    roleIds?: number[],
    accreditationIds?: number[]
  ): Promise<Response> {
    return NetworkManager.fetch(
      BuildUri(
        '/event/export/badges/{eventId}',
        { eventId },
        { forRole: roleIds, forAccreditation: accreditationIds }
      )
    )
  }

  getBulkBadgeInfo(
    eventId: number
  ): Promise<{ total: number; limit: number; pages: number }> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/badges/info', { eventId })
    )
  }

  getBulkBadgePdf(
    eventId: number,
    page: number = 0,
    limit: number = 100
  ): Promise<Response> {
    return NetworkManager.fetch(
      BuildUri('/event/{eventId}/badges/bulk', { eventId }, { page, limit })
    )
  }
}

export default new EventEndpoints()
